import { Injectable } from '@angular/core';
import { Socket } from 'ngx-socket-io';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { SocketChatNotification, SocketNotificationType } from 'src/app/shared/constants/enum';
import { ToastAlertService } from 'src/app/shared/utils/toast.service';
import { ISocketChatNotification, ISocketNotification, ISocketTyping } from '../domain/sockets.model';
import { ActivatedRoute, Router } from '@angular/router';

@Injectable({
  providedIn: 'root'
})
export class SocketService {
  url = '';
  lastChatRoom;
  notifications: Subject<ISocketNotification> = new Subject<ISocketNotification>();
  messages: Subject<ISocketChatNotification> = new Subject<ISocketChatNotification>();
  chatSubs: Subscription[] = [];
  startTyping: Subject<ISocketTyping> = new Subject<ISocketTyping>();
  endTyping: Subject<ISocketTyping> = new Subject<ISocketTyping>();
  onlineUsers: string[] = []
  onlineUsersUpdate: Subject<any> = new Subject();
  constructor(private socket: Socket,
    private toastAlertService: ToastAlertService, private router: Router) { }

  // SAN-1490 — the backend authenticates the socket handshake with the
  // httpOnly accessToken cookie, so the cookie must be sent cross-origin.
  setUrl(url: string) {
    this.url = url;
    this.socket.disconnect()
    // SAN-1490: send the httpOnly accessToken cookie (scoped to the apiUrl host)
    // with the socket handshake, so the backend can authenticate the socket and
    // only let it join this user's own rooms. No token is exposed to JS.
    this.socket = new Socket({ url: url, options: { withCredentials: true } })
  }

  // SAN-1490: re-handshake so the socket carries the session cookie set by a
  // login that happened after the socket was opened (in-app login does not
  // reload the page). Rooms are re-joined by the caller afterwards.
  reconnect() {
    if (!this.url) {
      return;
    }
    this.socket.disconnect();
    this.socket.connect();
  }

  listenToRoom(id: string) {
    // SAN-1490 — a handshake made before login is rejected; reconnect so the
    // new handshake carries the session cookie. The server joins the user's
    // own room itself; the joinRoom below is kept for older backends.
    this.socket.disconnect();
    this.socket.connect();
    this.socket.emit('joinRoom', '' + id);
    if (this.lastChatRoom) {
      this.socket.emit('joinRoom', this.lastChatRoom);
    }
    this.listenTOEvents();
    return this.socket.fromEvent('fetch-count').pipe(map(data => data));
  }


  listenTOEvents() {
    Object.values(SocketNotificationType).forEach((v) => {
      this.socket.fromEvent(v).pipe(map(data => data)).subscribe((res: ISocketNotification) => {

        this.notifications.next(res);
        if (v === SocketNotificationType.MESSAGE_NOTIFICATION_RECEIVED && this.router.url.includes('conversations')) {
          return
        }

        this.toastAlertService.showHtmlToast(res.title, res.icon, res.url)
      })
    })
  }


  leaveRoom(id: string) {
    this.socket.emit('leaveRoom', '' + id)
  }

  leaveLastChatRoom() {
    this.unsubscribeChatRoomEvents()
    this.leaveRoom(this.lastChatRoom)
    this.lastChatRoom = null
  }

  listenToChatRoom(id: string) {
    this.lastChatRoom = 'conversation_' + id;
    this.socket.emit('joinRoom', this.lastChatRoom);
    this.listenTOChatRooms();
  }

  listenTOChatRooms() {
    // One subscription per chat event. Previously only the last one was kept
    // in `chatSub`, so the other listeners leaked on every room join and each
    // incoming message was pushed once per leaked listener (SAN-1048).
    this.unsubscribeChatRoomEvents()
    this.chatSubs = Object.values(SocketChatNotification).map((v) =>
      this.socket.fromEvent(v).pipe(map(data => data)).subscribe((res: ISocketChatNotification) => {
        this.messages.next(res);
      })
    )
  }

  private unsubscribeChatRoomEvents() {
    this.chatSubs.forEach((s) => s.unsubscribe())
    this.chatSubs = []
  }

  emitToRoom(event, data) {
    this.socket.emit(event, data);
  }
  listenToTyping(event) {
    this.socket.fromEvent(event).pipe(map(data => data)).subscribe((res: ISocketTyping) => {
      if (res.type === 'start_typing') {
        this.startTyping.next(res);
      } else {
        this.endTyping.next(res);
      }
    });
  }
  listenToEndTyping(event) {
    this.socket.fromEvent(event).pipe(map(data => data)).subscribe((res: ISocketChatNotification) => {
      console.log('endTyping', res)
      // this.endTyping.next(res);
    });
  }

  listenToOnlineUsers(event) {
    this.socket.fromEvent(event).pipe(map(data => data)).subscribe((res: ISocketChatNotification) => {
      if (res) {
        this.onlineUsers = Object.values(res)
        this.onlineUsersUpdate.next(this.onlineUsers)
      }
      // this.endTyping.next(res);
    });
  }

  isUserOnline(userUUId: string) {
    return this.onlineUsers.includes(userUUId)
  }

}
